<template>
  <div class="main-container">
    <div>
      <!-- Header Section -->
      <v-row class="pa-2 header-row">
        <v-col cols="12" class="d-flex justify-space-between align-center">
          <v-btn icon class="back-button" @click="confirmBackNavigation">
            <v-icon class="white--text">mdi-arrow-left</v-icon>
          </v-btn>
          <!-- Conditionally hide the Add Job button if the role is Salesperson -->
          <v-btn
            v-if="isTechnician || !isSalesperson"
            color="#ff3b47"
            class="add-job-button my-4 mx-4"
            dark
            @click="handleAddJobClick"
          >
            Add Job
          </v-btn>
        </v-col>
      </v-row>

      <!-- Loader while fetching data -->
      <v-row v-if="loading" justify="center" align="center" class="loader-row">
        <v-progress-circular indeterminate color="#ff3b47"></v-progress-circular>
      </v-row>

      <!-- Jobs Section -->
      <div v-if="!loading" class="jobs-container">
        <!-- Search and Filter -->
        <v-row justify="center" class="search-filter-row">
          <v-col cols="12" md="4">
            <v-text-field
              class="pa-3 white-search-field"
              filled
              dense
              rounded
              v-model="search"
              append-icon="mdi-magnify"
              label="Search Jobs"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedStatus"
              :items="[...statusOptions, 'Clear filter']"
              placeholder="Filter by Status"
              filled
              dense
              rounded
              class="my-3"
              @change="handleSelectChange"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Jobs Cards -->
        <v-row>
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            v-for="(job, index) in paginatedJobs"
            :key="job.id || index"
          >
            <v-card class="modern-card" elevation="6" hover>
              <!-- Header with Status, Job Number, and Extra Info Button -->
              <v-row class="d-flex justify-end pr-3 pt-3 align-center">
                <!-- Status Chip -->
                <v-chip
                  :color="getChipColor(job.status)"
                  dark
                  label
                  class="status-chip ml-2"
                >
                  {{ job.status || 'Unknown' }}
                </v-chip>

                <!-- Job Number Chip -->
                <v-chip
                  color="grey"
                  dark
                  label
                  class="job-number-chip ml-2"
                >
                  Job #: {{ job.jobNumber || 'Unknown' }}
                </v-chip>

                <!-- Extra Info Button -->
                <v-btn
                  icon
                  @click="openExtraInfo(job)"
                  title="Extra Info"
                  class="ml-2"
                >
                  <v-icon color="#ff3b47">mdi-information</v-icon>
                </v-btn>
              </v-row>

              <!-- Card Content -->
              <v-card-text class="pa-4">
                <!-- Date Aligned to Right -->
                <v-row justify="end">
                  <v-col cols="12" class="py-2 text-right">
                   
                  </v-col>
                </v-row>

                <!-- Job Details -->
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Customer Name:</span>
                    <span class="font-weight-medium">{{ job.customerName || 'Unknown' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Seal Type:</span>
                    <span class="font-weight-medium">{{ job.jobName || 'Unknown' }}</span>
                  </v-col>
                </v-row>
                <!-- New Fields Added Below -->
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Created Date:</span>
                    <span class="font-weight-medium">{{ formatDate(job.currentDate) || 'N/A' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Grand Total:</span>
                    <span class="font-weight-medium">{{ formatCurrency(job.grandTotal) || 'R0.00' }}</span>
                  </v-col>
                </v-row>
              </v-card-text>

              <!-- Card Actions -->
              <v-card-actions class="d-flex justify-between align-center">
                <v-btn
                  rounded
                  dark
                  color="#ff3b47"
                  @click="handleCardClick(job)"
                  class="open-job-button flex-grow-1 mr-2"
                >
                  Open Job
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- Load More Button -->
        <v-row justify="center" v-if="canLoadMore">
          <v-btn
            color="#ff3b47"
            @click="loadMore"
            :disabled="!canLoadMore"
            class="load-more-button"
          >
            Load More
          </v-btn>
        </v-row>
      </div>
    </div>

    <!-- Confirmation Dialog for Back Button -->
    <v-dialog v-model="backDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>
          Are you sure you want to go back? Unsaved changes might be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="#ff3b47" text @click="backDialog = false">Cancel</v-btn>
          <v-btn color="#28a745" text @click="goBack">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Extra Info Dialog -->
    <v-dialog v-model="extraInfoDialog.visible" max-width="500" class="extra-info-dialog">
      <v-card>
        <v-card-title class="headline dialog-title">
          Extra Information
          <v-spacer></v-spacer>
          <v-btn icon @click="extraInfoDialog.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-list dense>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Job Number:</strong> {{ extraInfoJob.jobNumber || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Customer Name:</strong> {{ extraInfoJob.customerName || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Seal Type:</strong> {{ extraInfoJob.sealType || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Current Date:</strong> {{ formatDate(extraInfoJob.currentDate) || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Grand Total:</strong> {{ formatCurrency(extraInfoJob.grandTotal) || 'R0.00' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  <strong>Comment:</strong> {{ extraInfoJob.comment || 'N/A' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar Notification -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc 
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

export default {
  name: "AllJobs",
  data() {
    return {
      backDialog: false,
      items: [],
      search: "",
      selectedStatus: "",
      statusOptions: [
        { text: "Inspection", value: "Inspection" },
        { text: "Costing Complete", value: "Costing Complete" },
        { text: "Delayed", value: "Delayed" },
        { text: "Inprogress", value: "Inprogress" },
        { text: "Ready", value: "Ready" },
        { text: "Clear filter", value: "Clear filter" }, // Added for clearing filter
      ],
      userRole: "",
      userID: "",
      customers: [],
      visibleCount: 12, // Updated to 12
      loadIncrement: 12, // Updated to 12 for consistency
      loading: true, // Manage loading state
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      extraInfoDialog: {
        visible: false,
      },
      extraInfoJob: {},
    };
  },
  computed: {
    isSalesperson() {
      return this.userRole === "Salesperson";
    },
    isTechnician() {
      return this.userRole === "Technician";
    },
    filteredItems() {
      let filtered = this.items;

      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            (item.jobName && item.jobName.toLowerCase().includes(searchLower)) ||
            (item.jobNumber &&
              item.jobNumber.toString().toLowerCase().includes(searchLower)) ||
            (item.customerName &&
              item.customerName.toLowerCase().includes(searchLower)) ||
            (item.sealType && item.sealType.toLowerCase().includes(searchLower))
        );
      }

      if (this.selectedStatus && this.selectedStatus !== "Clear filter") {
        filtered = filtered.filter((item) => item.status === this.selectedStatus);
      }

      // Sort the filtered items by jobNumber from largest to smallest
      filtered.sort((a, b) => b.jobNumber - a.jobNumber);

      return filtered;
    },
    paginatedJobs() {
      return this.filteredItems.slice(0, this.visibleCount);
    },
    canLoadMore() {
      return this.visibleCount < this.filteredItems.length;
    },
  },
  watch: {
    search() {
      this.resetPagination();
    },
    selectedStatus() {
      this.resetPagination();
    },
  },
  async created() {
    await this.fetchUserRole();
    await this.fetchCustomers();
    await this.fetchJobs();
    this.loading = false; // Set loading to false after fetching jobs
  },
  methods: {
    confirmBackNavigation() {
    this.backDialog = true;
  },
  // Add the goBack method to route to /Activity
  goBack() {
    this.backDialog = false;
    this.$router.push("/Activity");
  },
    async fetchUserRole() {
      try {
        const user = auth.currentUser;
        if (user) {
          const uid = user.uid;
          this.userID = uid;
          const userDoc = await getDoc(doc(db, "users", uid));
          if (userDoc.exists()) {
            this.userRole = userDoc.data().role || "";
            localStorage.setItem("role", this.userRole); // Ensure 'role' is stored in localStorage
          } else {
            console.warn("No user document found!");
          }
        } else {
          console.warn("No user is currently logged in.");
          this.userRole = "Guest"; // Default role if no user is logged in
          localStorage.setItem("role", this.userRole);
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
        this.snackbarMessage = "Failed to fetch user role.";
        this.snackbarColor = "error";
        this.snackbar = true;
      }
    },
    async fetchCustomers() {
      try {
        const customersSnapshot = await getDocs(collection(db, "customers"));
        this.customers = customersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          companyName: doc.data().companyName || "Unknown Company",
          contactPerson: doc.data().contactPerson || "Unknown Contact",
        }));
      } catch (error) {
        console.error("Error fetching customers:", error);
        this.snackbarMessage = "Failed to fetch customers.";
        this.snackbarColor = "error";
        this.snackbar = true;
      }
    },
    async fetchJobs() {
      try {
        const user = auth.currentUser;
        if (user) {
          const userID = user.uid;
          let jobsQuery;

          // Define the statuses you want to include
          const allowedStatuses = [
            "Inspection",
            "Costing Complete",
            "Delayed",
            "Inprogress",
            "Ready",
          ];

          // Fetch jobs based on the user's role and allowed statuses
          if (this.userRole === "Engineer") {
            jobsQuery = query(
              collection(db, "jobs"),
              where("engineer.userID", "==", userID),
              where("status", "in", allowedStatuses)
            );
          } else if (this.userRole === "Technician") {
            jobsQuery = query(
              collection(db, "jobs"),
              where("technician.userID", "==", userID),
              where("status", "in", allowedStatuses)
            );
          } else if (this.userRole === "Salesperson") {
            jobsQuery = query(
              collection(db, "jobs"),
              where("salesperson.userID", "==", userID),
              where("status", "in", allowedStatuses)
            );
          } else {
            console.warn("User role is not Engineer, Technician, or Salesperson.");
            this.snackbarMessage = "Unauthorized access.";
            this.snackbarColor = "error";
            this.snackbar = true;
            return;
          }

          const querySnapshot = await getDocs(jobsQuery);
          const jobs = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            jobs.push({
              id: doc.id,
              jobName: data.jobName || "Unnamed Job",
              jobNumber: data.jobNumber || "",
              imageUrl: data.imageUrl || "@/assets/default_image.jpg",
              createdDate: data.createdDate || '',
              status: data.status || "Quote",
              customerName: this.getCustomerName(data.customerName) || "Unknown Customer",
              sealType: data.jobName || "Unknown Seal Type",
              currentDate: data.currentDate || '',
              grandTotal: data.grandTotal || "0.00",
              comment: data.comment || "",
            });
          });

          // Sort jobs by jobNumber from largest to smallest
          jobs.sort((a, b) => b.jobNumber - a.jobNumber);

          this.items = jobs;
        } else {
          console.warn("No user is currently logged in.");
          this.snackbarMessage = "Please log in to view jobs.";
          this.snackbarColor = "warning";
          this.snackbar = true;
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
        this.snackbarMessage = "Failed to fetch jobs.";
        this.snackbarColor = "error";
        this.snackbar = true;
      }
    },
    getCustomerName(customerName) {
      // Assuming customerName is already the name, return it directly
      // If it's an ID, uncomment the following lines and adjust accordingly
      /*
      const customer = this.customers.find((cust) => cust.docId === customerId);
      return customer
        ? customer.companyName || customer.contactPerson || "Customer Name Unavailable"
        : "Customer Name Unavailable";
      */
      return customerName;
    },
    navigateToFunctions() {
      this.backDialog = false;
      this.$router.push("/Functions");
    },
    navigateTo(item) {
      if (item.id) {
        localStorage.setItem("selectedJob", item.id);
        const userRole = this.userRole;

        // Route based on user role
        if (userRole === "Salesperson") {
          this.$router.push("/SalesVB");
        } else if (userRole === "Technician") {
          this.$router.push("/TechVB");
        } else if (userRole === "Engineer") {
          this.$router.push("/ViewJob");
        } else {
          // Default fallback, if no specific role is found
          this.$router.push({ name: "ViewJob" });
        }
      } else {
        console.warn("Job ID is missing. Cannot navigate.");
        this.snackbarMessage = "Invalid job data.";
        this.snackbarColor = "error";
        this.snackbar = true;
      }
    },
    handleAddJobClick() {
      // Route based on user role
      if (this.isTechnician) {
        this.$router.push("/TechAddJob");
      } else {
        this.$router.push("/addJob");
      }
    },
    loadMore() {
      this.visibleCount += this.loadIncrement;
    },
    resetPagination() {
      this.visibleCount = this.loadIncrement;
    },
    handleCardClick(job) {
      this.navigateTo(job);
    },
    openExtraInfo(job) {
      this.extraInfoJob = job;
      this.extraInfoDialog.visible = true;
    },
    getChipColor(status) {
      if (!status) return 'grey';
      switch(status.toLowerCase()) {
        case 'inspection':
          return 'orange';
        case 'costing complete':
          return 'teal';
        case 'delayed':
          return 'brown';
        case 'inprogress':
          return 'light-blue';
        case 'ready':
          return 'green';
        case 'completed':
          return 'dark-green';
        default:
          return 'grey';
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      if (isNaN(date)) return dateStr;
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    formatCurrency(amount) {
      if (!amount) return 'R0.00';
      // Assuming the amount is a number or a string that can be converted to a number
      const number = typeof amount === 'string' ? parseFloat(amount) : amount;
      if (isNaN(number)) return 'R0.00';
      return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(number);
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

body,
div,
span,
v-card-title,
v-card-text,
v-row,
v-col,
v-card,
v-chip,
v-avatar,
v-toolbar-title {
  font-family: "Montserrat", sans-serif;
}

.main-container {
  background-color: #121212;
  min-height: 100vh;
}

.header-row {
  background-color: #1e1e1e;
  border-bottom: 1px solid #333;
}

.jobs-container {
  background-color: #ffffff;
  border-radius: 20px 20px 0 0;
  padding: 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.search-filter-row {
  margin-bottom: 24px;
}

.modern-card {
  background-color: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  margin: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.modern-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.status-chip {
  font-size: 0.75rem;
  text-transform: capitalize;
}

.job-number-chip {
  font-size: 0.75rem;
  text-transform: uppercase;
}

.open-job-button {
  font-weight: 600;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.open-job-button:hover {
  background-color: #e63946;
  transform: scale(1.02);
}

.back-button {
  color: white;
  border-radius: 50%;
  transition: transform 0.3s ease-in-out;
}

.back-button:hover {
  transform: scale(1.1);
}

.back-button .v-icon {
  color: white;
}

.add-job-button {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.add-job-button:hover {
  background-color: #e63946;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.load-more-button {
  font-weight: 600;
  margin-top: 16px;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.load-more-button:hover {
  background-color: #e63946;
  transform: scale(1.02);
}

.white-search-field .v-input__control {
  background-color: white;
  border-radius: 8px;
}

.white-search-field .v-label {
  color: #555;
}

.white-search-field .v-input__icon--append .v-icon {
  color: #555;
}

.text-caption {
  font-size: 0.8rem;
  color: #757575;
}

.font-weight-medium {
  font-weight: 500;
  color: #333;
}

.loader-row {
  margin-top: 50px;
}

.v-dialog .v-card {
  border-radius: 16px;
}

.v-toolbar {
  border-bottom: 1px solid #ddd;
}

.v-toolbar-title {
  font-weight: 700;
}

.v-chip {
  font-size: 0.75rem;
  text-transform: capitalize;
}

.v-avatar {
  border: 2px solid white;
}

/* Extra Info Dialog Styling */
.extra-info-dialog .v-card {
  background-color: #f9f9f9;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}
.extra-info-dialog .dialog-title {
  background-color: #ff3b47;
  color: white;
  font-weight: 600;
}
.extra-info-dialog .v-card-text {
  padding: 16px;
}
.extra-info-dialog .v-list-item {
  border-bottom: 1px solid #e0e0e0;
}
.extra-info-dialog .v-list-item:last-child {
  border-bottom: none;
}

/* Responsive Adjustments */
@media (max-width: 600px) {
  .modern-card {
    margin: 8px;
    height: auto; /* Adjusted to auto for better responsiveness */
  }
  .v-col {
    margin-bottom: 16px;
  }
}
</style>
